import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import {
  useAgentSocket,
  type ErrorSignal,
  type GovernanceMetadata,
  type SendExtras,
  type SendRejectedSignal
} from '../../lib/useAgentSocket';
import type {
  AgentDescriptor,
  AgentRunState,
  BlockActionValueWire,
  ConversationSummaryWire,
  InvocationChipWire,
  McpWriteCommittedFrame,
  PenroseSessionInfo,
  QueuedSendWire,
  QuestionAnswerWire
} from '../../lib/agentProtocol';
import { hasEvictableInstance } from './agentInstanceSelectors';
import {
  countPendingRows,
  emptyTranscript,
  firstAssistantMessage,
  firstUserMessage,
  type Transcript
} from '../../pages/agentTranscript';
import { requestAutoName, setAutoNameTransport } from '../../lib/agentAutoName';
import { isIDE } from '../../lib/isIDE';
import { getOpenItem, openItemActiveIds } from '../../lib/openItemStore';
import {
  deriveActiveIds,
  originFromHost,
  type AgentChatHost,
  type OriginItem
} from './agentChatHost';
import { AgentWriteToast } from './AgentWriteToast';
import {
  MAX_INSTANCES,
  MAX_LABEL_LENGTH,
  type AgentInstanceView
} from './agentInstanceTypes';
import './AgentChat.css';

/** The three auto-accept modes available in the agent panel. */
export type AutoAcceptMode = 'ask-everything' | 'auto-accept-edits' | 'auto-accept-all';

/** One entry in the provider-managed create-toast slot (Task 3.4). */
interface CreateToast {
  /** Stable React key + auto-dismiss timer identity. */
  key: number;
  /** Which `create-*` variant of `<AgentWriteToast>` to render. */
  mode: 'create-document' | 'create-work-item';
  /** New item title (interpolated into the copy). */
  title?: string;
  /** New item id (click-through). Absent on the real path → no link. */
  id?: string;
}

/**
 * Maps the committed-write `tool` name to the create-toast mode, or `undefined`
 * for non-create writes (write/update/delete) — those are editor-level and the
 * provider must NOT surface a create toast for them.
 */
const CREATE_TOAST_MODE: Record<string, CreateToast['mode'] | undefined> = {
  create_document: 'create-document',
  create_work_item: 'create-work-item'
};

/**
 * The shared agent session exposed to every `<AgentChat>` (Phase 5, Option A).
 *
 * There is exactly one of these per IDE process: the standalone panel and both
 * embedded chats are views onto the SAME conversation. `reportHost` lets the
 * currently-mounted-and-focused chat declare which item it belongs to and how
 * to build the session hand-off; `send` stamps each turn with the host that
 * was reported most recently (per-turn active-context capture).
 */
export interface AgentSession {
  agents: AgentDescriptor[];
  /**
   * All live agent instances, keyed by `instanceId` (multi-agent-instances
   * Phase 2, AC-U1). Each view merges the socket's per-instance bucket with the
   * provider-owned, process-scoped display `label`. Always contains at least
   * {@link focusedInstanceId} (the spec's "at least one instance always" rule).
   * The flat accessors below (`selectedAgentId`/`runState`/`transcript`/
   * `autoAcceptMode`) mirror the FOCUSED instance for backward-compat consumers.
   */
  instances: Record<string, AgentInstanceView>;
  /** The currently-focused instance id (drives the flat focused-view + outbound posts). */
  focusedInstanceId: string;
  /**
   * Switch focus to `id` — the flat focused-view + every outbound instance-scoped
   * post follow it (AC-U1). The tab strip (Task 2.3) calls this on a tab click.
   */
  focusInstance(id: string): void;
  /**
   * Spawn a new instance: posts `create-instance`; the harness mints the id and
   * announces it via `instance-created`, at which point the provider registers a
   * default **Agent N** label (US-1, AC-U4). No-op at the cap (see
   * {@link canCreateInstance}).
   *
   * When `inheritFromInstanceId` is supplied (agent-instance-picker AC-W2: the
   * **+** button next to the picker inherits from the currently-focused
   * instance), the provider captures that source instance's `selectedAgentId`
   * and applies it to the newly-spawned bucket once `instance-created` lands —
   * so a user spawning a sibling from an `inst-1` that selected `claude-code`
   * never has to re-pick the adapter on `inst-2`.
   */
  createInstance(options?: { inheritFromInstanceId?: string }): void;
  /**
   * Read the per-instance transcript for `id` (agent-instance-picker Task 2.2).
   * The picker's title/preview fallback chain feeds the FOCUSED instance's
   * transcript into `titleForRow`/`previewForRow`, and the dropdown row mapper
   * threads each background instance's transcript through the same selectors —
   * background transcripts live in the socket-level instance buckets, so the
   * provider exposes them via this single getter rather than having the picker
   * reach into the socket directly. Returns an empty transcript when `id` is
   * unknown (e.g. a row mid-close-race): a clean fallback to `"New chat"` is
   * preferable to a crash.
   */
  getInstanceTranscript(id: string): Transcript;
  /**
   * Tear down `id`: posts `close-instance` (US-6, AC-U5 harness side).
   * Conversation history — close = DEACTIVATE: the conversation survives in
   * {@link history} and can be reopened via {@link activateConversation};
   * permanent removal is {@link deleteConversation}.
   */
  closeInstance(id: string): void;
  /**
   * The persisted, NON-LIVE conversations (conversation history), most-recent
   * first — the picker renders these below its live rows. Empty against an
   * older harness that never sends a `conversations-snapshot`.
   */
  history: ConversationSummaryWire[];
  /**
   * Re-activate a persisted conversation from {@link history} into the live
   * pool. The harness mints a fresh instance (evicting the LRU idle one when
   * the pool is at the cap) and auto-focuses it via `instance-created`.
   * No-op client-side when the pool is full AND nothing is evictable — the
   * picker surfaces {@link createBlockedReason} instead.
   */
  activateConversation(conversationId: string): void;
  /**
   * Permanently delete a persisted, NON-LIVE conversation from
   * {@link history}. The picker's history rows are the only call surface
   * (confirm-gated); a live conversation is rejected harness-side.
   */
  deleteConversation(conversationId: string): void;
  /**
   * Rename `id`'s process-scoped label (US-4, AC-U4). Trims, rejects empty
   * (keeps the prior label), caps at 40 chars. NOT persisted to Supabase.
   *
   * `opts.source` (agent-instance-picker Task 3.2, AC-N3) distinguishes a
   * manual user rename from an auto-named write so the sticky-flag guard
   * (`manuallyNamed`) works:
   *
   *   - `'manual'` (the default — every existing call site behaves unchanged):
   *     sets `manuallyNamed=true`. A subsequent auto-name dispatch is gated
   *     out at the write-time guard so a user's rename mid-stream is never
   *     clobbered (AC-N5).
   *   - `'auto'`: sets `autoNamed=true`. The Phase-3 auto-name service is the
   *     sole caller — the provider's dispatch path checks `manuallyNamed`
   *     BEFORE invoking this, AND re-checks it inside `renameInstance` (the
   *     locked-spec write-time edge case) so a rename that fired between the
   *     fire-time check and the auto-name resolution still wins.
   */
  renameInstance(
    id: string,
    label: string,
    opts?: { source?: 'manual' | 'auto' }
  ): void;
  /**
   * `false` only when a new conversation genuinely cannot start: the live pool
   * is at the cap (4) AND no instance is evictable (every one is running or
   * holds pending cards). At the cap with an idle instance this stays `true` —
   * creation succeeds by deactivating the LRU idle conversation to history
   * (conversation history). The "+ New conversation" affordances disable on
   * `false` and surface {@link createBlockedReason}.
   */
  canCreateInstance: boolean;
  /**
   * Why creation is blocked, for one consistent message surface:
   * `'all-running'` when {@link canCreateInstance} is `false` (the only
   * blocked case — the pool is full of running/pending instances), else
   * `null`.
   */
  createBlockedReason: 'all-running' | null;
  selectedAgentId: string | null;
  runState: AgentRunState;
  transcript: Transcript;
  governance: GovernanceMetadata | null;
  /** Current auto-accept mode. Defaults to 'ask-everything'. */
  autoAcceptMode: AutoAcceptMode;
  select(id: string): void;
  /**
   * Send a user prompt. The optional `invocation` (Build 0.2 — Turn Anatomy) is
   * the sent-turn record of what tool / skill / flow the turn invoked; it is
   * stamped onto the user row (rendered as a chip) and rides the outbound frame.
   * The composer that populates it ships in Build 0.3; a free-text turn omits it.
   *
   * Curated-Surface 0.3 (Chip → Directive Assembly) adds `extras`: the compiled
   * `invocations` (stack order) + the authenticated org `scope`. Optional +
   * back-compat — a free-text-only send omits it (NFR-003).
   */
  send(text: string, invocation?: InvocationChipWire, extras?: SendExtras, transcriptText?: string): void;
  stop(): void;
  /**
   * Optimistically resolve the approval row and send the decision to the
   * backend. `options.persist` carries the **Always allow** choice
   * (persisted-tool-permissions Task 2.3).
   */
  decideApproval(
    id: string,
    outcome: 'allow' | 'deny',
    options?: { persist?: boolean; selectedIds?: string[] }
  ): void;
  /**
   * Save a committable block's parked draft (Build 0.2 — Turn Anatomy &
   * Uncommitted State). Optimistically marks the per-`approvalId` commit state
   * 'committing' and resolves the parked `mcp-write` approval with an `allow`.
   * `selectedIds` carries the still-checked subset for a multi-item confirm-bar.
   */
  saveDraft(approvalId: string, selectedIds?: string[]): void;
  /**
   * Discard a committable block's parked draft (Build 0.2). Optimistically marks
   * the per-`approvalId` commit state 'discarded' and resolves the parked
   * `mcp-write` approval with a `deny`.
   */
  discardDraft(approvalId: string): void;
  /**
   * Optimistically resolve the question row and send the positional answers to
   * the backend (agent-structured-questions Phase 2). Only ever called from a
   * user click on Submit — there is no auto-answer path.
   */
  answerQuestion(id: string, answers: QuestionAnswerWire[]): void;
  /**
   * Submit an interactive block action (Interactive Block Library). Optimistically
   * resolves the matching `block` row and posts a `block-action` message to the
   * backend. Mirrors {@link answerQuestion} — only ever called from a user
   * interaction on a block, never auto-submitted.
   */
  submitBlockAction(
    id: string,
    blockId: string,
    value: BlockActionValueWire,
    summary: string
  ): void;
  /** Update the auto-accept mode and sync it to the backend. */
  setAutoAccept(mode: AutoAcceptMode): void;
  /**
   * Set the focused instance's per-session model / effort (agent-effort-selector
   * Task 3.3). Posts a `set-session-config` frame for the focused instance
   * carrying ONLY the keys present in `config` (so `{ effort }` alone omits
   * `modelId` on the wire) and optimistically updates the focused instance's
   * `modelId` / `effort` to EXACTLY the passed values for the present keys.
   * Passing a key with `undefined` clears it locally — this optimistic write
   * OWNS the reset-to-Default-(CLI) flow, because the reconnect-snapshot merge
   * in `useAgentSocket` uses `?? prev` and never clears these fields. A key
   * omitted from `config` is left untouched.
   */
  setSessionConfig(config: {
    modelId?: string;
    effort?: string;
    permissionMode?: string;
    thinking?: string;
    approvalPolicy?: string;
    sandbox?: string;
    ultracode?: boolean;
  }): void;
  /**
   * Current pending follow-up queue, FIFO head-first. Populated from the
   * `queue-state` backend frame; empty until the first frame arrives.
   */
  queue: QueuedSendWire[];
  /** Convenience count — always equals `queue.length`. */
  queueCount: number;
  /**
   * Transient over-cap-rejection signal, mirrored from the backend's dedicated
   * `queue-full` frame. NOT a transcript row — a cap rejection is not a run
   * error. `<AgentChat>` watches `nonce` (strictly incrementing, so repeated
   * rejections re-trigger) to raise the brief queue-full toast + restore the
   * draft (AC-U7). `null` until the first rejection.
   */
  queueFull: { message: string; nonce: number } | null;
  /**
   * Curated-Surface 0.3 (Chip → Directive Assembly), Task 8 — transient
   * reject→restore signal, mirrored from the backend's `send-rejected` frame.
   * NOT a transcript row. `<AgentChat>` watches `nonce` (strictly incrementing)
   * to restore the just-sent chips as an editable draft and announce `detail` for
   * a11y when the rejection targets the focused instance. `null` until the first
   * rejection. See {@link SendRejectedSignal}.
   */
  sendRejected: SendRejectedSignal | null;
  /**
   * Curated-Surface 0.3 follow-up — transient turn-error signal, mirrored from
   * the backend's `error` frame. The error STILL appears as a transcript row;
   * this signal is an additional observable `<AgentChat>` watches (by strictly-
   * incrementing `nonce`) to clear the post-Send compiled-call dock for the
   * focused instance when a turn aborts. `null` until the first error. See
   * {@link ErrorSignal}.
   */
  errorSignal: ErrorSignal | null;
  /**
   * Remove a queued follow-up message by its stable id. Posts a
   * `queue-remove` frame to the backend (AC-U5 / AC-P5).
   */
  removeQueued(id: string): void;
  /**
   * "Free up room" (agent-compaction Phase 4) — summarize the focused instance's
   * older context NOW. Optimistically flips the focused instance into its meter
   * working state and posts a `compact` command; the resulting `compaction` frame
   * drops a transcript marker + the meter, the same as the agent's auto path.
   */
  compact(): void;
  /**
   * Regenerate the `create_project` overview to match an EDITED project name
   * (004b-create-project Phase 3b). Posts a `regenerate-overview` round-trip
   * scoped to the pending approval's `approvalId` + the current edited `name`,
   * and resolves with the agent's fresh overview (rejects on failure / socket
   * close). Fired only by an explicit press of the card's "Regenerate overview
   * to match" control — never auto-fired. Passes straight through from the
   * socket; the originating popover scopes the result back to its own card.
   */
  regenerateOverview(approvalId: string, name: string): Promise<string>;
  /**
   * Called by the mounted `<AgentChat>` to declare its host + how to build the
   * base session hand-off (token, org, role). Returns a cleanup that clears
   * the report on unmount. `buildBaseSessionInfo` carries everything EXCEPT
   * the active ids — the provider overlays the host-derived ids per turn.
   */
  reportHost(
    host: AgentChatHost,
    buildBaseSessionInfo: () => PenroseSessionInfo
  ): () => void;
  /**
   * Subscribe to committed-write frames the backend emits after a governed MCP
   * write lands (`mcp-write-committed`). Every subscribed `listener` is invoked
   * with each frame; listeners do their OWN filtering (e.g. by `targetId`).
   * Returns an unsubscribe function that removes the listener.
   */
  subscribeMcpWriteCommitted(
    listener: (frame: McpWriteCommittedFrame) => void
  ): () => void;
}

const AgentSessionContext = createContext<AgentSession | null>(null);

/** The latest host + base-session-info reported by a mounted `<AgentChat>`. */
interface HostReport {
  host: AgentChatHost;
  buildBaseSessionInfo: () => PenroseSessionInfo;
}

/**
 * Shallow field-equality for two `PenroseSessionInfo` values. `PenroseSessionInfo`
 * is a flat record of primitives, so a per-field comparison is exact. Used to
 * decide whether a fresh `reportHost` actually changed the base session (a
 * Supabase token rotation included) versus merely handed back a new object
 * identity for the same values — only a real change should drive a re-post.
 */
function sameSessionInfo(
  a: PenroseSessionInfo | null,
  b: PenroseSessionInfo | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.accessToken === b.accessToken &&
    a.supabaseUrl === b.supabaseUrl &&
    a.supabaseAnonKey === b.supabaseAnonKey &&
    a.orgId === b.orgId &&
    a.orgName === b.orgName &&
    a.functionRoleId === b.functionRoleId &&
    a.functionRoleName === b.functionRoleName &&
    a.activeDocId === b.activeDocId &&
    a.activeWorkItemId === b.activeWorkItemId &&
    a.activeDocTitle === b.activeDocTitle &&
    a.activeWorkItemTitle === b.activeWorkItemTitle
  );
}

export interface AgentSessionProviderProps {
  children: ReactNode;
  /** Injectable for tests — defaults to the real backend WebSocket. */
  socketFactory?: () => WebSocket;
}

/**
 * Process-scoped agent-session provider — Phase 5, Option A.
 *
 * Mounted ONCE, above the router, IDE-only (see `main.tsx`). It calls
 * `useAgentSocket` a single time so the WebSocket + transcript + run state
 * outlive any single `<AgentChat>`'s mount: navigating from the panel to a
 * Work Item chat shows the same conversation. A non-IDE build never mounts
 * this provider, so no socket is ever opened.
 */
export function AgentSessionProvider({
  children,
  socketFactory
}: AgentSessionProviderProps) {
  // The latest host report from whichever <AgentChat> is mounted. A ref, not
  // state — it must not re-render the provider (and the whole tree under it)
  // on every chat mount/unmount.
  const hostReportRef = useRef<HostReport | null>(null);

  // Subscribers to committed-write frames, held in a ref (NOT state): adding or
  // removing a listener must not re-render the provider or the tree under it.
  // The dispatcher iterates this set on each `mcp-write-committed` frame.
  const mcpWriteListenersRef = useRef<
    Set<(frame: McpWriteCommittedFrame) => void>
  >(new Set());

  // The latest reported BASE session hand-off (token, org, role — NO active
  // ids), held as state so a Supabase token rotation reaches `useAgentSocket`.
  //
  // Why state, not just the ref: `useAgentSocket`'s token-refresh effect keys
  // on `sessionInfo`. The provider sits above the router and cannot read the
  // shell session, so without this it would only ever pass `sessionInfoForSend`
  // (a stable callback) — the refresh effect would then fire once at mount and
  // never again, and a token that rotates while a chat is idle (no `send`)
  // would never reach the backend. Threading the reported base session through
  // state re-fires the existing refresh effect on every token change, so the
  // backend re-posts a fresh `session` frame WITHOUT tearing down the socket.
  // The per-turn `sessionInfoForSend` still overlays the active doc/work-item
  // ids at `send` time; the idle refresh only needs the token, so this reported
  // base session — used as-is, WITHOUT the per-turn active-id overlay — is the
  // correct payload here.
  const [reportedSession, setReportedSession] =
    useState<PenroseSessionInfo | null>(null);

  // Per-turn capture: build the session hand-off for THIS turn from the
  // latest-reported host. `undefined` when no chat has reported yet.
  const sessionInfoForSend = useCallback((): PenroseSessionInfo | undefined => {
    const report = hostReportRef.current;
    if (!report) return undefined;
    const base = report.buildBaseSessionInfo();
    // An EMBEDDED chat (document/workItem) carries its own route's id, which is
    // authoritative for that view. The standalone PANEL has no item of its own,
    // so it overlays the cross-route open-item store — i.e. the Document /
    // Work Item the user most recently OPENED in an editor route. This is what
    // closes the bug: the panel used to report both ids null (no active
    // context), so the agent edited the wrong item.
    const ids =
      report.host.kind === 'panel'
        ? openItemActiveIds(getOpenItem())
        : deriveActiveIds(report.host);
    return {
      ...base,
      activeDocId: ids.activeDocId,
      activeWorkItemId: ids.activeWorkItemId,
      // Carry the open item's title alongside its id so the backend can NAME
      // the open item authoritatively (get_active_context + the gateway-free
      // prompt line), not just identify it by id.
      activeDocTitle: ids.activeDocTitle,
      activeWorkItemTitle: ids.activeWorkItemTitle,
      // PRD 1.8 — the current project a skill run's output Note links to.
      activeProjectId: ids.activeProjectId
    };
  }, []);

  // Dispatch a committed-write frame to every subscribed listener. Each
  // invocation is isolated in try/catch so one listener throwing cannot stop
  // the rest from running (or break the socket's `onmessage` handler).
  const dispatchMcpWriteCommitted = useCallback(
    (frame: McpWriteCommittedFrame) => {
      for (const listener of mcpWriteListenersRef.current) {
        try {
          listener(frame);
        } catch {
          // A misbehaving listener must not affect the others — swallow it.
        }
      }
    },
    []
  );

  // Refs used by the Phase-3.2 auto-name dispatch. `onFirstTurnComplete` fires
  // synchronously inside `useAgentSocket`'s message handler — by the time it
  // runs, the bucket's `transcript` already includes the first user message
  // AND the just-finalized first assistant message (the fold ran before the
  // emission). Reading them via a ref against the latest socket-instances map
  // is the correct synchronous path; closing over a stale render of
  // `socket.instances` would miss the freshly-folded assistant text.
  //
  // We type these refs against `InstanceState`-shaped values (imported below)
  // and `AgentSession['renameInstance']`. Initial values are `null`/`{}` —
  // the refs are rewritten right after `useAgentSocket` returns (for the
  // instances map) and right after `renameInstance` is defined (for the
  // renamer), so the closure inside `onFirstTurnComplete` always reads the
  // freshest values at fire time.
  const socketInstancesRef = useRef<
    Record<
      string,
      {
        transcript: Transcript;
        selectedAgentId: string | null;
      }
    >
  >({});
  // The latest `manuallyNamed` map (write-time AC-N5 guard) and the latest
  // `renameInstance` callback — both updated after their definitions below.
  const manuallyNamedRefForFireTime = useRef<Record<string, boolean>>({});
  const renameInstanceRef = useRef<AgentSession['renameInstance'] | null>(null);

  const socket = useAgentSocket({
    ...(socketFactory ? { socketFactory } : {}),
    sessionInfo: reportedSession ?? undefined,
    sessionInfoForSend,
    onMcpWriteCommitted: dispatchMcpWriteCommitted,
    // PHASE-3.2 — auto-name dispatch (Task 3.2, AC-N1..N5). The socket fires
    // this exactly once per instance on the first streaming → complete
    // transition (AC-N1); idempotence is enforced inside `useAgentSocket` via
    // the bucket's `firstTurnComplete` flag, so this handler can fire-and-
    // forget without its own dedupe. The dispatch is silent on failure
    // (AC-N4) — the picker keeps its Phase 2 fallback title.
    onFirstTurnComplete: ({ instanceId }) => {
      const bucket = socketInstancesRef.current[instanceId];
      if (!bucket) return;
      // AC-N5 fire-time guard — a user who renamed before the first turn
      // completes never sees an auto-name dispatch at all.
      if (manuallyNamedRefForFireTime.current[instanceId]) return;
      if (!bucket.selectedAgentId) return; // no agent client to dispatch to
      const firstUser = firstUserMessage(bucket.transcript);
      const firstAssistant = firstAssistantMessage(bucket.transcript);
      // Defensive — the seam fires after the first assistant `complete`, so by
      // construction both rows exist; bail silently if a future fold change
      // breaks that invariant rather than dispatching with empty content.
      if (!firstUser || !firstAssistant) return;
      const ctrl = new AbortController();
      requestAutoName({
        instanceId,
        selectedAgentId: bucket.selectedAgentId,
        firstUserMessage: firstUser.text,
        firstAssistantMessage: firstAssistant.text,
        signal: ctrl.signal
      })
        .then((autoName) => {
          // AC-N5 write-time guard — re-check inside `renameInstance` via
          // `manuallyNamedRef` so a user rename that landed while the request
          // was in flight (the locked-spec edge case) still wins. We pass
          // `source: 'auto'`; the guard inside `renameInstance` is the
          // authoritative one.
          renameInstanceRef.current?.(instanceId, autoName, { source: 'auto' });
        })
        .catch(() => {
          // AC-N4 — silent failure. The picker keeps the Phase 2 fallback.
        });
    }
  });

  // Keep the socket-instances ref fresh on every render so the fire-time
  // closure inside `onFirstTurnComplete` always sees the freshly-folded
  // transcript. `socket.instances` carries every bucket field; we narrow the
  // ref type to just the fields the dispatch needs (`transcript`,
  // `selectedAgentId`).
  socketInstancesRef.current = socket.instances;

  // agent-instance-picker Task 3.3 — register the real WS-backed auto-name
  // transport at mount. The `agentAutoName` service is intentionally
  // stateless (no React imports) and pulls its dispatch boundary via
  // `setAutoNameTransport(...)`; this is the IDE-only wiring that routes
  // the call through the harness's `request-auto-name` round-trip. The
  // cleanup clears the registration so an unmounted provider never leaves
  // a stale closure pointing at a dead socket (the next provider mount
  // overwrites it, and a non-IDE build never registers one).
  const socketRequestAutoName = socket.requestAutoName;
  useEffect(() => {
    setAutoNameTransport(({
      instanceId,
      selectedAgentId,
      firstUserMessage,
      firstAssistantMessage,
      signal
    }) =>
      socketRequestAutoName({
        instanceId,
        selectedAgentId,
        firstUserMessage,
        firstAssistantMessage,
        signal
      })
    );
    return () => {
      setAutoNameTransport(null);
    };
  }, [socketRequestAutoName]);

  // ---- Multi-agent instances (Task 2.2) -----------------------------------
  //
  // The socket owns the per-instance buckets (transcript / runState / selection
  // / auto-accept / pendingCount). The provider owns the process-scoped DISPLAY
  // labels (default `Agent N`, user-renamable) — labels are NOT persisted to
  // Supabase (spec: process-scoped). `instanceLabels` maps instanceId → label;
  // it is keyed independently of the socket map so a label survives a transient
  // bucket churn within the same process session.
  const [instanceLabels, setInstanceLabels] = useState<Record<string, string>>(
    {}
  );
  // Per-instance monotonic stamp of the most recent user `send` (agent-instance-
  // picker Task 2.1). `null` is the sentinel for "never sent" — the recency
  // selector trails null-stamped rows after every sent one. Keyed by instanceId,
  // bumped in the wrapped `send` below for the focused instance only. Inbound
  // backend frames (delta / approval-request / question-request) do NOT touch
  // this — US-9 / AC-W3: background activity must not promote a chat row to the
  // top of the picker's recency list.
  const [lastUserMessageAt, setLastUserMessageAt] = useState<
    Record<string, number | null>
  >({});
  // Per-instance "user has manually renamed" flag (Task 2.1). Flipped to `true`
  // exclusively in `renameInstance` (after the input passes the trim+non-empty
  // guard) so a rejected rename never falsely flags an instance as manually
  // named. The picker's `titleForRow` reads this; Phase 3's auto-name service
  // uses the same flag as the "do not auto-name" guard.
  const [manuallyNamed, setManuallyNamed] = useState<Record<string, boolean>>(
    {}
  );
  // Per-instance "auto-name service successfully wrote this label" flag
  // (agent-instance-picker Task 3.2, AC-N3). Set exclusively by `renameInstance`
  // when invoked with `opts.source === 'auto'`. The picker's `titleForRow`
  // (Task 3.3) reads this between the manual flag and the user-message
  // truncation in the full AC-F1 precedence chain. Distinct from `manuallyNamed`
  // — the manual flag is the sticky guard, so the two are mutually exclusive
  // by construction (the write-time check inside `renameInstance` skips an
  // auto write when the instance is already manually-named).
  const [autoNamed, setAutoNamed] = useState<Record<string, boolean>>({});
  // Per-instance home item (Conversations-in-Document/Work-Item launchers,
  // Phase 1). Process-scoped, mirroring `instanceLabels`: keyed by instanceId,
  // never persisted to Supabase. Seeded at creation from the active host (the
  // value passed to `createInstance`) so an instance carries its origin even
  // before the harness's `instances-snapshot` echo lands; the merged `instances`
  // view prefers the socket bucket's snapshot echo and falls back to this map.
  // `null` is the explicit "home-less (standalone panel)" value; an absent key
  // means "not yet seeded" (the view then falls back to `null`).
  const [instanceOrigins, setInstanceOrigins] = useState<
    Record<string, OriginItem | null>
  >({});
  // The home item to assign to the NEXT freshly-announced instance bucket
  // (Phase 1). Set synchronously in `createInstance` from the active host's
  // derived origin, consumed exactly once by the new-instance effect that
  // watches `socketInstances` keys for a new id (mirrors `pendingInheritRef`).
  const pendingOriginRef = useRef<OriginItem | null>(null);
  // Monotonic "Agent N" counter. Reuse-on-close is intentionally NOT done — we
  // may skip holes (v1 simplicity, allowed by spec "reuse number on close
  // optional"): closing Agent 2 then spawning leaves the next as Agent 3, never
  // recycling 2. This keeps numbering stable + non-surprising while a session
  // runs.
  const nextAgentNumberRef = useRef(1);

  // Derive a default `Agent N` label for any instance the socket announced that
  // the provider has not labelled yet — covers the default `inst-1` on connect,
  // a `create-instance`/`instance-created` spawn, and `instances-snapshot`
  // restore. Runs as an effect off the socket's instance keys so it reacts to
  // EVERY way a bucket can appear, without the provider needing its own socket
  // message listener.
  const socketInstances = socket.instances;
  useEffect(() => {
    setInstanceLabels((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(socketInstances)) {
        // agent-conversation-persistence Task 3.5 (US-1) — a RESTORED instance
        // carries its DURABLE label on the socket bucket (folded from
        // `instances-snapshot`). It must drive the display label so the restored
        // conversation shows the name the user gave it, NOT a fresh `Agent N`.
        const restored = socketInstances[id]?.label;
        if (next[id] === undefined) {
          // First sighting: seed with the durable restored label when present,
          // else the monotonic `Agent N` default for a live (never-restored)
          // instance. A later manual `renameInstance` overwrites this slot.
          next[id] = restored ?? `Agent ${nextAgentNumberRef.current++}`;
          changed = true;
        } else if (
          restored !== undefined &&
          restored !== next[id] &&
          // Don't clobber a manual rename — the user's name always wins (the
          // restored label is durable, but an explicit rename is more so). Read
          // the LATEST manual flag via the ref (declared before `useAgentSocket`
          // so it is in scope here) so a rename committed this render is honored.
          // A restore snapshot for an instance that existed BEFORE the snapshot
          // (e.g. the default `inst-1`) lands here.
          !manuallyNamedRefForFireTime.current[id]
        ) {
          next[id] = restored;
          changed = true;
        }
      }
      // Drop labels for instances the socket no longer knows about (closed) so
      // the label map cannot grow unbounded across a long session.
      for (const id of Object.keys(next)) {
        if (socketInstances[id] === undefined) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Mirror the same lifecycle for the picker's recency-stamp map and the
    // manual-name flag map (Task 2.1): every freshly-announced instance gets a
    // `null` stamp + `manuallyNamed:false`, and closed ids drop out so neither
    // map can grow unbounded.
    setLastUserMessageAt((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(socketInstances)) {
        if (!(id in next)) {
          next[id] = null;
          changed = true;
        }
      }
      for (const id of Object.keys(next)) {
        if (socketInstances[id] === undefined) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setManuallyNamed((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(socketInstances)) {
        if (!(id in next)) {
          next[id] = false;
          changed = true;
        }
      }
      for (const id of Object.keys(next)) {
        if (socketInstances[id] === undefined) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Mirror the same lifecycle for the auto-name flag map (Task 3.2): every
    // freshly-announced instance starts `autoNamed: false` (auto-name has not
    // run yet); closed ids drop out so the map can't grow unbounded.
    setAutoNamed((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(socketInstances)) {
        if (!(id in next)) {
          next[id] = false;
          changed = true;
        }
      }
      for (const id of Object.keys(next)) {
        if (socketInstances[id] === undefined) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Phase 1 — drop home-item entries for closed instances so the map cannot
    // grow unbounded across a long session. (Seeding a new instance's origin is
    // done in the dedicated new-instance effect below from `pendingOriginRef`,
    // NOT here, because only a create that supplied an origin should seed one —
    // the default `inst-1` and a reconnect-restored bucket must NOT be defaulted
    // to a home item; they acquire it from the snapshot echo when present.)
    setInstanceOrigins((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (socketInstances[id] === undefined) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [socketInstances]);

  // The exposed instance views: merge each socket bucket with its provider
  // label. Falls back to the raw id for the one render before the label effect
  // commits its `Agent N` (an honest placeholder, never `undefined`).
  const instances = useMemo<Record<string, AgentInstanceView>>(() => {
    const out: Record<string, AgentInstanceView> = {};
    for (const [id, bucket] of Object.entries(socketInstances)) {
      // The badge count (AC-U6) is derived LIVE from the bucket's own
      // transcript — a background `approval-request`/`question-request` folds
      // into the originating instance's transcript (Task 2.1), so deriving here
      // increments only that instance's badge the instant the request arrives,
      // never a foreign instance's. The reconnect `instances-snapshot` count
      // (`bucket.pendingCount`) is taken as a floor via `Math.max` so a replay
      // that has not yet re-folded its pending requests still shows the count.
      const derived = countPendingRows(bucket.transcript);
      out[id] = {
        id,
        label: instanceLabels[id] ?? id,
        runState: bucket.runState,
        selectedAgentId: bucket.selectedAgentId,
        autoAcceptMode: bucket.autoAcceptMode,
        pendingCount: Math.max(derived, bucket.pendingCount),
        // `lastUserMessageAt` lazily defaults to `null` for any id the
        // init-effect has not committed yet (one render at most after a fresh
        // `instance-created` lands) — honest sentinel, never `undefined`.
        lastUserMessageAt: lastUserMessageAt[id] ?? null,
        manuallyNamed: manuallyNamed[id] ?? false,
        autoNamed: autoNamed[id] ?? false,
        // Mirror the socket bucket's Phase 3 emission flag — `false` until the
        // first assistant turn completes, then `true` forever (agent-instance-
        // picker Task 3.1). Phase 3.2's auto-name dispatch reads it via the
        // `onFirstTurnComplete` callback (the seam that fires exactly once).
        firstTurnComplete: bucket.firstTurnComplete,
        // Mirror the socket bucket's per-session model / effort (agent-effort-
        // selector Task 3.3). `setSessionConfig` optimistically writes these on
        // the bucket; the view passes them straight through (undefined =
        // Default (CLI)).
        modelId: bucket.modelId,
        effort: bucket.effort,
        // Native-controls expansion — mirror the per-instance native knobs
        // through to the view (the VM reads them to drive the mode/thinking/
        // approval/sandbox controls).
        permissionMode: bucket.permissionMode,
        thinking: bucket.thinking,
        approvalPolicy: bucket.approvalPolicy,
        sandbox: bucket.sandbox,
        ultracode: bucket.ultracode,
        // Mirror the socket bucket's APPLIED-config baseline (agent-effort-
        // selector US-4). Captured by the socket at the idle→running transition;
        // the VM diffs modelId/effort against these to clear the pending strip
        // once a change has ridden a turn.
        appliedModelId: bucket.appliedModelId,
        appliedEffort: bucket.appliedEffort,
        // Mirror the socket bucket's restore scalars (agent-conversation-
        // persistence Task 3.5), folded from `instances-snapshot`. `restoreStatus`
        // drives the read-only-history / agent-unavailable indicators + the Send
        // gate via `useRestoreVm`; `conversationId` is the persisted id this
        // instance restored from. Both `undefined` for a live instance.
        conversationId: bucket.conversationId,
        restoreStatus: bucket.restoreStatus,
        // Phase 1 (Conversations-in-Document/Work-Item launchers) — the
        // conversation's home item. Prefer the socket bucket's snapshot echo
        // (harness-authoritative, survives restore); fall back to the
        // provider-tracked create-time origin for an instance that predates a
        // snapshot echo; `null` (home-less) when neither is present.
        originItem: bucket.originItem ?? instanceOrigins[id] ?? null,
        // agent-compaction Phase 4 — mirror the socket bucket's context-meter
        // state straight through (the meter reads it for the focused instance).
        contextUsage: bucket.contextUsage,
        compacting: bucket.compacting,
        needsFreshConversation: bucket.needsFreshConversation,
        // 006-claude-subagent-support — mirror the socket bucket's discovered
        // subagents through to the view; the ⚒ Tools drawer reads it (for the
        // focused instance) to build the Subagents tab. `undefined` ⇒ the agent
        // supports no subagents (the drawer's empty-with-explanation state).
        availableSubagents: bucket.availableSubagents,
        // 006-claude-subagent-support phase 4 — mirror the socket bucket's
        // per-instance subagent-activity buffer through to the view. `AgentChat`
        // reads it for the FOCUSED instance only and feeds the pure phase-3
        // `deriveSubagentRows`, so the limited activity row is scoped structurally.
        subagentActivity: bucket.subagentActivity
      };
    }
    return out;
  }, [
    socketInstances,
    instanceLabels,
    lastUserMessageAt,
    manuallyNamed,
    autoNamed,
    instanceOrigins
  ]);

  // Conversation history — creation is possible below the cap OR when an idle
  // instance can be deactivated to history to make room (the harness performs
  // the actual eviction). Blocked ONLY when all 4 are running / pending.
  const canCreateInstance =
    Object.keys(socketInstances).length < MAX_INSTANCES || hasEvictableInstance(instances);
  const createBlockedReason: 'all-running' | null = canCreateInstance ? null : 'all-running';

  // Pending agent-id to apply to the next freshly-announced instance bucket
  // (agent-instance-picker AC-W2). Set by `createInstance({ inheritFromInstanceId })`,
  // consumed exactly once by the effect that watches socketInstances keys for a
  // new id. A ref (not state) because the consumer effect must not re-run on
  // the set itself — it runs on the NEW instance arriving.
  const pendingInheritRef = useRef<string | null>(null);

  const socketCreateInstance = socket.createInstance;
  const socketSelect = socket.select;
  const createInstance = useCallback(
    (options?: { inheritFromInstanceId?: string }) => {
      // Guard the blocked case on the client too (the harness also rejects an
      // un-evictable create) so a spammed click cannot post a doomed
      // `create-instance` frame. At the cap WITH an evictable idle instance the
      // post goes through — the harness deactivates the LRU idle conversation
      // to history and mints the new one (conversation history).
      if (
        Object.keys(socketInstances).length >= MAX_INSTANCES &&
        !hasEvictableInstance(instances)
      ) {
        return;
      }
      // Snapshot the source's `selectedAgentId` BEFORE posting create-instance,
      // so a downstream `instance-created` (auto-focus → fold into the new id)
      // sees the captured value even if the source instance somehow churned in
      // the same tick.
      const sourceId = options?.inheritFromInstanceId;
      if (sourceId) {
        const source = socketInstances[sourceId];
        if (source?.selectedAgentId) {
          pendingInheritRef.current = source.selectedAgentId;
        }
      }
      // Phase 1 (Conversations-in-Document/Work-Item launchers) — derive the new
      // conversation's home item from the ACTIVE host at creation time: a
      // document/workItem host homes it to that item (caching its title); the
      // standalone `panel` host yields `null` (a home-less, general thread). The
      // home item is FIXED here for the conversation's life (no re-homing). Stash
      // it in `pendingOriginRef` so the new-instance effect seeds it onto the
      // freshly-announced bucket, and ride it on the `create-instance` frame so
      // the harness persists + echoes it back (survives a session restore).
      const origin = hostReportRef.current
        ? originFromHost(hostReportRef.current.host)
        : null;
      pendingOriginRef.current = origin;
      socketCreateInstance(origin);
    },
    [socketCreateInstance, socketInstances, instances]
  );

  // Apply the pending inherit `selectedAgentId` to the next-arriving instance
  // bucket. Runs whenever `socketInstances` changes: the moment a NEW id
  // appears (the harness's `instance-created` fold) while `pendingInheritRef`
  // is set, we post `select` for the harness's focused instance — which is now
  // the new one (auto-focus on `instance-created`). The ref is cleared
  // synchronously so the inheritance fires for exactly one spawn (AC-W2).
  const knownInstanceIdsRef = useRef<Set<string>>(new Set(Object.keys(socketInstances)));
  useEffect(() => {
    const known = knownInstanceIdsRef.current;
    const current = new Set(Object.keys(socketInstances));
    let newId: string | null = null;
    for (const id of current) {
      if (!known.has(id)) {
        newId = id;
        break;
      }
    }
    knownInstanceIdsRef.current = current;
    if (newId && pendingInheritRef.current) {
      const agentId = pendingInheritRef.current;
      pendingInheritRef.current = null;
      // socket.select posts `select` for the harness's focused id, which the
      // `instance-created` handler set to the new instance synchronously — so
      // this lands on the freshly-spawned bucket.
      socketSelect(agentId);
    }
    // Phase 1 — seed the new instance's home item from the create-time origin
    // captured in `pendingOriginRef`. Consumed exactly once per spawn (the ref is
    // cleared after). A `null` origin (panel thread) is recorded explicitly so
    // the merged view reads "home-less" rather than falling through to a later
    // snapshot's value; the harness echo (when it lands) still reconciles via the
    // socket bucket, which the view prefers.
    if (newId && pendingOriginRef.current !== undefined) {
      const origin = pendingOriginRef.current;
      pendingOriginRef.current = null;
      const seededId = newId;
      setInstanceOrigins((prev) =>
        prev[seededId] === origin ? prev : { ...prev, [seededId]: origin }
      );
    }
  }, [socketInstances, socketSelect]);

  const getInstanceTranscript = useCallback(
    (id: string): Transcript => {
      const bucket = socketInstances[id];
      return bucket ? bucket.transcript : emptyTranscript();
    },
    [socketInstances]
  );

  // Conversation history — re-activate a persisted conversation. Same blocked
  // pre-guard as `createInstance` (activation at the cap also needs an
  // evictable idle instance); the harness performs the eviction + mint and
  // auto-focuses the fresh instance via `instance-created`.
  const socketActivateConversation = socket.activateConversation;
  const activateConversation = useCallback(
    (conversationId: string) => {
      if (
        Object.keys(socketInstances).length >= MAX_INSTANCES &&
        !hasEvictableInstance(instances)
      ) {
        return;
      }
      socketActivateConversation(conversationId);
    },
    [socketActivateConversation, socketInstances, instances]
  );

  // Ref-mirror of the `manuallyNamed` map so an in-flight auto-name resolve
  // (Task 3.2 dispatch path) can read the LATEST flag synchronously at write
  // time without closing over a stale render. The spec's AC-N5 edge case: the
  // user renames mid-stream WHILE the auto-name service is still in flight;
  // the closure captured a stale `false`, but the write must still skip. The
  // SAME map is also mirrored into `manuallyNamedRefForFireTime` (declared
  // before `useAgentSocket` so the `onFirstTurnComplete` callback can read
  // it without a TDZ); both refs point at the latest snapshot.
  const manuallyNamedRef = useRef(manuallyNamed);
  manuallyNamedRef.current = manuallyNamed;
  manuallyNamedRefForFireTime.current = manuallyNamed;

  const renameInstance = useCallback(
    (
      id: string,
      label: string,
      opts?: { source?: 'manual' | 'auto' }
    ) => {
      const source = opts?.source ?? 'manual';
      const trimmed = label.trim();
      // Reject empty / whitespace-only — keep the prior label (Locked UX) AND
      // leave `manuallyNamed` / `autoNamed` untouched so a rejected rename
      // never falsely flags the instance (which would mis-suppress the title
      // fallback AND, for an auto-rejection, mis-promote the row in
      // titleForRow).
      if (trimmed.length === 0) return;
      // Write-time manual-sticky guard (AC-N5): an auto-name resolve that
      // races a user rename must never overwrite the manual label. The
      // `manuallyNamedRef` reads the LATEST flag, defending against the
      // stale-closure edge case where the user renamed mid-stream.
      if (source === 'auto' && manuallyNamedRef.current[id]) return;
      const capped = trimmed.slice(0, MAX_LABEL_LENGTH);
      setInstanceLabels((prev) => ({ ...prev, [id]: capped }));
      if (source === 'manual') {
        setManuallyNamed((prev) => ({ ...prev, [id]: true }));
      } else {
        setAutoNamed((prev) => ({ ...prev, [id]: true }));
      }
    },
    []
  );
  // Mirror the latest `renameInstance` into the ref so the in-flight auto-name
  // resolve (set up at provider mount) always invokes the freshest callback.
  renameInstanceRef.current = renameInstance;

  // Wrap `socket.send` to stamp the focused instance's `lastUserMessageAt`
  // (Task 2.1) — the recency selector reads this for AC-W3. Stamp BEFORE
  // posting so a synchronous downstream re-render sees the bump immediately;
  // `Date.now()` is monotonic-enough for v1 (multiple sends in the same tick
  // collapse to one stamp, which is fine — the recency sort is stable).
  const socketSend = socket.send;
  const focusedInstanceId = socket.focusedInstanceId;
  const focusedInstanceIdRef = useRef(focusedInstanceId);
  focusedInstanceIdRef.current = focusedInstanceId;
  const send = useCallback(
    (text: string, invocation?: InvocationChipWire, extras?: SendExtras) => {
      const id = focusedInstanceIdRef.current;
      setLastUserMessageAt((prev) => ({ ...prev, [id]: Date.now() }));
      socketSend(text, invocation, extras);
    },
    [socketSend]
  );

  const subscribeMcpWriteCommitted = useCallback<
    AgentSession['subscribeMcpWriteCommitted']
  >((listener) => {
    const listeners = mcpWriteListenersRef.current;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Provider-managed create-toast slot (Task 3.4, AC-11). The provider is the
  // single owner of create toasts: when a `create_document` / `create_work_item`
  // write commits there is no editor open for the just-created item, so the
  // editor-level handlers (Tasks 3.2/3.3) deliberately ignore create_* frames.
  // Each entry removes itself on dismiss; `key` keeps React reconciliation and
  // the auto-dismiss timer stable across re-renders.
  const [createToasts, setCreateToasts] = useState<CreateToast[]>([]);
  const nextToastKeyRef = useRef(0);

  // Internal listener on the SAME committed-write bus. On a create_* frame it
  // pushes one toast into the slot; everything else (write/update/delete) is
  // left to the editor-level handlers. Cleans up on provider unmount.
  useEffect(() => {
    const unsubscribe = subscribeMcpWriteCommitted((frame) => {
      const mode = CREATE_TOAST_MODE[frame.tool];
      if (!mode) return;
      const key = nextToastKeyRef.current++;
      setCreateToasts((prev) => [
        ...prev,
        {
          key,
          mode,
          // Title from the frame; the click-through id is optional — on the real
          // path the harness cannot know `createdId` before the write commits,
          // so `<AgentWriteToast>` renders the copy without a link when absent.
          ...(frame.createdTitle !== undefined
            ? { title: frame.createdTitle }
            : {}),
          ...(frame.createdId !== undefined ? { id: frame.createdId } : {})
        }
      ]);
    });
    return unsubscribe;
  }, [subscribeMcpWriteCommitted]);

  const dismissCreateToast = useCallback((key: number) => {
    setCreateToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const reportHost = useCallback<AgentSession['reportHost']>(
    (host, buildBaseSessionInfo) => {
      hostReportRef.current = { host, buildBaseSessionInfo };
      // Mirror the reported base session into state so a token rotation re-fires
      // `useAgentSocket`'s refresh effect. `setReportedSession` only commits a
      // new value when a field actually changed (token included), so a chat
      // reporting an identity-only change does not churn the socket effect.
      const nextBase = buildBaseSessionInfo();
      setReportedSession((prev) =>
        sameSessionInfo(prev, nextBase) ? prev : nextBase
      );
      return () => {
        // Clear only if this exact report is still the active one — a later
        // mount may have replaced it (tie-break: last-reported host wins). The
        // `reportedSession` state is intentionally NOT cleared on unmount: the
        // socket outlives any single chat, and the next chat to mount will
        // report and overwrite it. Nulling it here would drop the token from a
        // live socket between an unmount and the next mount.
        if (
          hostReportRef.current?.host === host &&
          hostReportRef.current?.buildBaseSessionInfo === buildBaseSessionInfo
        ) {
          hostReportRef.current = null;
        }
      };
    },
    []
  );

  const value = useMemo<AgentSession>(
    () => ({
      agents: socket.agents,
      instances,
      focusedInstanceId: socket.focusedInstanceId,
      focusInstance: socket.focusInstance,
      createInstance,
      closeInstance: socket.closeInstance,
      history: socket.conversations,
      activateConversation,
      deleteConversation: socket.deleteConversation,
      renameInstance,
      canCreateInstance,
      createBlockedReason,
      getInstanceTranscript,
      selectedAgentId: socket.selectedAgentId,
      runState: socket.runState,
      transcript: socket.transcript,
      governance: socket.governance,
      autoAcceptMode: socket.autoAcceptMode,
      select: socket.select,
      send,
      stop: socket.stop,
      decideApproval: socket.decideApproval,
      saveDraft: socket.saveDraft,
      discardDraft: socket.discardDraft,
      answerQuestion: socket.answerQuestion,
      submitBlockAction: socket.submitBlockAction,
      setAutoAccept: socket.setAutoAccept,
      setSessionConfig: socket.setSessionConfig,
      queue: socket.queue,
      queueCount: socket.queue.length,
      queueFull: socket.queueFull,
      sendRejected: socket.sendRejected,
      errorSignal: socket.errorSignal,
      removeQueued: socket.removeQueued,
      compact: socket.compact,
      regenerateOverview: socket.regenerateOverview,
      reportHost,
      subscribeMcpWriteCommitted
    }),
    [
      socket.agents,
      instances,
      socket.focusedInstanceId,
      socket.focusInstance,
      createInstance,
      socket.closeInstance,
      socket.conversations,
      activateConversation,
      socket.deleteConversation,
      renameInstance,
      canCreateInstance,
      createBlockedReason,
      getInstanceTranscript,
      socket.selectedAgentId,
      socket.runState,
      socket.transcript,
      socket.governance,
      socket.autoAcceptMode,
      socket.select,
      send,
      socket.stop,
      socket.decideApproval,
      socket.saveDraft,
      socket.discardDraft,
      socket.answerQuestion,
      socket.submitBlockAction,
      socket.setAutoAccept,
      socket.setSessionConfig,
      socket.queue,
      socket.queueFull,
      socket.sendRejected,
      socket.errorSignal,
      socket.removeQueued,
      socket.compact,
      socket.regenerateOverview,
      reportHost,
      subscribeMcpWriteCommitted
    ]
  );

  return (
    <AgentSessionContext.Provider value={value}>
      {children}
      {/* Provider-managed create-toast slot, portaled to the viewport's
       * bottom-right (no editor is open for a just-created item). 4000ms
       * auto-dismiss is delegated to <AgentWriteToast>/<Toast>; each toast
       * removes itself from the slot on dismiss. */}
      {createToasts.length > 0
        ? createPortal(
            createToasts.map((toast) => (
              <div key={toast.key} className="agent-create-toast-slot">
                <AgentWriteToast
                  mode={toast.mode}
                  {...(toast.title !== undefined ? { title: toast.title } : {})}
                  {...(toast.id !== undefined ? { id: toast.id } : {})}
                  onDismiss={() => dismissCreateToast(toast.key)}
                />
              </div>
            )),
            document.body
          )
        : null}
    </AgentSessionContext.Provider>
  );
}

/**
 * Consume the shared agent session. Throws if used outside
 * `<AgentSessionProvider>` — a clear failure beats a silent `null`.
 */
export function useAgentSession(): AgentSession {
  const ctx = useContext(AgentSessionContext);
  if (!ctx) {
    throw new Error('useAgentSession must be used within AgentSessionProvider');
  }
  return ctx;
}

/**
 * IDE-only wrapper for `main.tsx`: mounts `<AgentSessionProvider>` in the IDE
 * build and is a transparent pass-through in the plain web build (no provider,
 * no socket — preserving the Phase 3 Rules-of-Hooks gate).
 */
export function MaybeAgentSessionProvider({ children }: { children: ReactNode }) {
  return isIDE() ? (
    <AgentSessionProvider>{children}</AgentSessionProvider>
  ) : (
    <>{children}</>
  );
}
